'use client';
import {
  type CatalogProviderLoaderResult,
  type CatalogProviderMarker,
  type CatalogProviderSummary,
  type NormalizedCatalogSearchFilters
} from '@/features/catalog';
import { CANADA_CENTER, CANADA_ZOOM } from '../mapCenters';
import { BreedResultsHeader, type BreadcrumbItem } from './BreedResultsHeader';
import { CollapsibleSearchHeader } from './CollapsibleSearchHeader';
import { LazyMapView } from './LazyMapView';
import { PuppySearchResults } from './PuppySearchResults';
import { usePuppySearchExperience } from './usePuppySearchExperience';
import styles from './styles/puppySearchExperience.module.scss';

export type PuppySearchExperienceProps = {
  initialFilters: NormalizedCatalogSearchFilters;
  initialProviders: CatalogProviderSummary[];
  initialPagination: CatalogProviderLoaderResult['pagination'];
  initialMarkers?: CatalogProviderMarker[];
  breedName?: string;
  nicknames?: string[] | null;
  provinceName?: string;
  breadcrumbItems?: BreadcrumbItem[];
  notice?: React.ReactNode;
  /** Server-rendered map image — bypasses hydration gate for LCP. */
  mapPlaceholder?: React.ReactNode;
};

export function PuppySearchExperience({
  initialFilters,
  initialProviders,
  initialPagination,
  initialMarkers,
  breedName,
  nicknames,
  provinceName,
  breadcrumbItems,
  notice,
  mapPlaceholder
}: PuppySearchExperienceProps) {
  const {
    isHeaderExpanded,
    setHeaderExpanded,
    search,
    providerMarkers,
    selectedProviderId,
    setSelectedProviderId,
    handleApplyFilters,
    handleMapSelect
  } = usePuppySearchExperience({
    initialFilters,
    initialProviders,
    initialPagination,
    initialMarkers
  });

  const mapCenter = CANADA_CENTER;
  const mapZoom = CANADA_ZOOM;

  // An empty, pin-less map reads as a low-supply signal (a hard-rule violation)
  // and burns LCP budget. Suppress the hero map until there is something to
  // show; if the visitor filters into results, it appears client-side.
  const hasResults = search.providers.length > 0 || providerMarkers.length > 0;

  return (
    <main
      className={`${styles.searchSurface} ${isHeaderExpanded ? styles.searchSurfaceExpanded : ''}`}
    >
      <CollapsibleSearchHeader
        selectedBreedLabel={breedName}
        filters={search.filters}
        onApplyFilters={handleApplyFilters}
        isExpanded={isHeaderExpanded}
        onExpandedChange={setHeaderExpanded}
      />
      {breedName && breadcrumbItems && (
        <BreedResultsHeader
          breedName={breedName}
          nicknames={nicknames}
          provinceName={provinceName}
          breadcrumbItems={breadcrumbItems}
          hasResults={hasResults}
        />
      )}
      {notice}
      {hasResults && (
        <section
          className={styles.heroMapSection}
          aria-label="Puppy search map"
        >
          <div className={styles.heroMapShell}>
            <LazyMapView
              mapSource="leaflet"
              providerMarkers={providerMarkers}
              onProviderSelect={handleMapSelect}
              center={mapCenter}
              zoom={mapZoom}
              disableMapMovement
              mapPlaceholder={mapPlaceholder}
            />
          </div>
        </section>
      )}
      {search.providers.length > 0 && (
        <div className={styles.resultsShell}>
          <PuppySearchResults
            providers={search.providers}
            pagination={search.pagination}
            filters={search.filters}
            status={search.status}
            onLoadMore={search.loadNextPage}
            selectedProviderId={selectedProviderId}
            onSelectProvider={setSelectedProviderId}
          />
        </div>
      )}
    </main>
  );
}
